import { useState, useEffect } from 'react'
import { Grid, Typography, makeStyles, IconButton, Container, Button } from '@material-ui/core'
import GetAppRoundedIcon from '@material-ui/icons/GetAppRounded'
import Panel from '../component/report/firsttime/Panel'
import DataTable from '../component/report/firsttime/DataTable'
import SearchAccordion from '../control/accordion/SearchAccordion'
import useAuthenStore from '../stores/authen/authenStore'
import { useSearchFirstTime, SearchKeywordType } from '../services/um/useSearchFirstTime'
import firstTimeExport from '../component/report/firsttime/report'
import moment from 'moment'
import useTranslate from '../i18n/useTranslate'
import { convertDateToFormatTH, convertDateToFormatEN } from '../utilities/date'
import { setPrefixName } from './../utilities/prefixName'

import ArrowBackRounded from '@material-ui/icons/ArrowBackRounded'
import { useHistory } from 'react-router'
import { useQueryParam } from 'use-query-params'
import useProjectType from '../services/um/lookups/useProjectType'
import useProjectStatus from '../services/um/lookups/useProjectStatus'
import useFaculty from '../services/um/lookups/useFaculty'

const FirstTime = () => {
    const translate = useTranslate()
    const lang = translate.languageStore.currentLanguage as 'th' | 'en'
    const classes = useStyles()
    const authenStore = useAuthenStore()
    const history = useHistory()

    const { searchFirstTime, dataList } = useSearchFirstTime()
    const { projectT } = useProjectType()
    const { projectStatus } = useProjectStatus()
    const { faculty } = useFaculty()
    const [keyword, setKeyword] = useQueryParam<string | null | undefined>('keyword')
    const [submittedType, setSubmittedType] = useQueryParam<number | null | undefined>('submittedType')
    const [projectType, setProjectType] = useQueryParam<number | null | undefined>('projectType')
    const [facultyID, setFacultyID] = useQueryParam<number | null | undefined>('facultyID')
    const [startDate, setStartDate] = useQueryParam<Date | string>('startDate')
    const [endDate, setEndDate] = useQueryParam<Date | string>('endDate')

    const [initialValue, setInitialValue] = useState<SearchKeywordType>({
        keyword: keyword,
        submittedType: submittedType ?? 1,
        projectType: projectType,
        facultyID: facultyID,
        startDate: startDate ?? moment().subtract(1, 'year').format('YYYY-MM-DD'),
        endDate: endDate ?? moment().format('YYYY-MM-DD'),
        userID: authenStore.profile.userId ?? 0,
    })

    const setSearchParam = (value: SearchKeywordType) => {
        setKeyword(value.keyword)
        setSubmittedType(value.submittedType ?? 1)
        setProjectType(value.projectType)
        setFacultyID(value.facultyID)
        setStartDate(moment(value.startDate).format('YYYY-MM-DD') ?? '')
        setEndDate(moment(value.endDate).format('YYYY-MM-DD') ?? '')
    }

    useEffect(() => {
        if (authenStore.profile.userId) {
            onSearch(initialValue)
        }
    }, [authenStore.profile.userId])

    const onSearch = async (values: SearchKeywordType) => {
        setInitialValue(values)
        searchFirstTime({
            keyword: values.keyword || null,
            submittedType: Number(values.submittedType) || 1,
            projectType: Number(values.projectType) || null,
            facultyID: Number(values.facultyID) || null,
            startDate: values.startDate,
            endDate: values.endDate,
            userID: authenStore.profile.userId ?? 0,
        })
        setSearchParam(values)
    }

    const onReset = (form: any) => {
        form.reset()
        setInitialValue({
            keyword: null,
            submittedType: 1,
            projectType: null,
            facultyID: null,
            startDate: moment().subtract(1, 'year').format('YYYY-MM-DD'),
            endDate: moment().format('YYYY-MM-DD'),
            userID: authenStore.profile.userId ?? 0,
        })
    }

    const getProjectTypeName = (value?: number | null) => {
        let result = null
        if (value) {
            result = projectT.find((item) => item.value === value?.toString())?.label
        }
        return result ?? '-'
    }

    const getProjectSubmitTypeName = (value?: number | null) => {
        let result = null
        if (value) {
            result = projectStatus.find((item) => item.value === value?.toString())?.label
        }
        return result ?? '-'
    }

    const getFacultyName = (value?: number | null) => {
        let result = null
        if (value) {
            result = faculty.find((item) => item.value === value?.toString())?.label
        }
        return result ?? '-'
    }

    const handleExportExcel = () => {
        firstTimeExport({
            header: {
                projectType: getProjectTypeName(projectType),
                projectSubmitType: getProjectSubmitTypeName(submittedType),
                faculty: getFacultyName(facultyID),
                startDate: convertDate(moment(startDate).format('YYYY-MM-DD')),
                endDate: convertDate(moment(endDate).format('YYYY-MM-DD')),
                keyword: keyword ?? '-',
            },
            content: dataList.map((item) => {
                return {
                    projectID: item.projectNumber,
                    receiveDate: convertDate(item.receiveDate),
                    projectSubmitType: item.submittedTypeName[lang], //รอ service
                    projectType: item.projectType.name[lang] ?? '-',
                    projectStatus: item.approveStatusName[lang] ?? '-',
                    researcherName: setPrefixName(item)[lang] ?? '-',
                    position: item.researcherTypeName[lang] ?? '-',
                    faculty: item.facultyName[lang] ?? '-',
                    educationLevelName: item.educationLevelName[lang] ?? '-',
                    projectNameTH: item.projectName['th'] ?? '-',
                    projectNameEN: item.projectName['en'] ?? '-',
                   
                }
            }),

            translate: (value: string) => translate.get(value),
        })
    }

    const convertDate = (date: string | null) => {
        if (lang === 'th' && date) {
            return convertDateToFormatTH(date)
        } else if (lang === 'en' && date) {
            return convertDateToFormatEN(date)
        } else {
            return '-'
        }
    }

    const onBack = () => {
        history.push('/dashboard')
    }

    return (
        <Container maxWidth="lg">
            <Grid className={classes.root}>
                <Grid xs={12} sm={12} md={12} lg={12}>
                    <SearchAccordion title={translate.get('UM.FIRST_TIME.HEADER')} isBack={true} onClickBack={onBack}>
                        <Panel onSearch={onSearch} onReset={onReset} initialValue={initialValue} />
                    </SearchAccordion>
                </Grid>
                <Grid container justify="space-between" alignItems="center" className={classes.container}>
                    <Grid item xs={12} md={6}>
                        <Typography variant="h6">
                            {translate.get('LABEL.SEARCH_TOTAL_RESULT', [`${dataList.length}`])}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Grid container justify="flex-end" spacing={2}>
                            <Grid item>
                                <Button
                                    size="large"
                                    variant="outlined"
                                    color="primary"
                                    startIcon={<GetAppRoundedIcon style={{ color: '#DB5F8E' }} />}
                                    onClick={handleExportExcel}
                                >
                                    <Typography variant="button">{translate.get('BUTTON.EXPORT')}</Typography>
                                </Button>
                            </Grid>
                        </Grid>
                    </Grid>

                    <Grid item xs={12}>
                        <DataTable dataList={dataList} />
                    </Grid>
                </Grid>
            </Grid>
        </Container>
    )
}

const useStyles = makeStyles(() => ({
    root: {
        width: '100%',
    },
    container: {
        marginTop: '20px',
        width: '100%',
    },
}))

export default FirstTime
